/* eslint-disable max-nested-callbacks */
import { CSVLink } from 'react-csv';
import { useAppSelector } from '../../hooks/store';
import {
  nightEndSelector,
  selectedWarehouseSelector,
} from 'src/state/selectors/delivery';
import { routingLocationsSelector } from 'src/state/selectors/locations';
import { GetLocation } from 'src/graphql/types/delivery';
import { getDeliveryWindowStr } from 'src/utils/date';
import { Button } from '@odekoteam/doppio';

const headers = [
  { label: 'location_id', key: 'locationId' },
  { label: 'business_name', key: 'businessName' },
  { label: 'city', key: 'city' },
  { label: 'state', key: 'state' },
  { label: 'postal_code', key: 'postalCode' },
  { label: 'cohort', key: 'cohort' },
  { label: 'service_time', key: 'serviceTime' },
  { label: 'automated_routing', key: 'automatedRouting' },
  { label: 'enforce_item_scanning', key: 'enforceItemScanning' },
  { label: 'deliver_within_store_hours', key: 'deliverWithinStoreHours' },
  { label: 'store_hours_confirmed', key: 'storeHoursConfirmed' },
  { label: 'scheduled_delivery_time', key: 'scheduledDeliveryTimeSource' },
];

type CsvRow = {
  metrobiEligible: string;
  locationId: string;
  businessName: string;
  city: string;
  state: string;
  postalCode: string;
  cohort?: string;
  serviceTime: number;
  automatedRouting: string;
  enforceItemScanning: string;
  deliverWithinStoreHours: string;
  storeHoursConfirmed: string;
  scheduledDeliveryTime?: string;
  scheduledDeliveryTimeSource?: string;
};

const DownloadLocationsCsv = ({
  locations,
  loading,
  showMetrobi = false,
}: {
  locations: GetLocation[];
  loading: boolean;
  showMetrobi?: boolean;
}): JSX.Element => {
  const nightEnd = useAppSelector(nightEndSelector);
  const selectedWarehouse = useAppSelector(selectedWarehouseSelector);
  const routingLocations = useAppSelector(routingLocationsSelector);
  const isDisabled = loading;

  const csvData: CsvRow[] = locations.map((location) => {
    // Get state-aware values for location
    const locationInState = routingLocations.find(
      (routingLocation) => routingLocation.locationId === location.id,
    );

    const locationServiceTime =
      locationInState?.serviceTime ?? location.serviceTime ?? 5;

    const locationScheduledDeliveryTimeSource =
      locationInState?.scheduledDeliveryTimeSource
        ? locationInState.scheduledDeliveryTimeSource
        : location.scheduledDeliveryTimeSource;

    const locationDeliverWithinStoreHours =
      locationInState?.deliverWithinStoreHours !== undefined
        ? locationInState.deliverWithinStoreHours
        : location.deliverWithinStoreHours;

    // Base row data with all fields
    const baseRow = {
      metrobiEligible:
        location.effectivelyExcluded === false ? 'true' : 'false',
      locationId: location.id,
      businessName: location.businessName,
      city: location.address.city,
      state: location.address.state,
      postalCode: location.address.postalCode,
      cohort: location.routingCohort?.name,
      serviceTime: locationServiceTime,
      automatedRouting: location.includeInAutomatedRouting ? 'true' : 'false',
      enforceItemScanning: location.enforceItemScanning ? 'true' : 'false',
      deliverWithinStoreHours: locationDeliverWithinStoreHours
        ? 'true'
        : 'false',
      storeHoursConfirmed: location.storeHoursConfirmed ? 'true' : 'false',
      scheduledDeliveryTimeSource: locationScheduledDeliveryTimeSource,
    };

    const deliveryWindowLabel = getDeliveryWindowStr({
      locationDeliveryWindow: location.locationDeliveryWindow,
      showDate: false,
    });

    return {
      ...baseRow,
      scheduledDeliveryTime: deliveryWindowLabel,
    };
  });

  return (
    <Button id="download-csv-button" isDisabled={isDisabled}>
      {loading ? (
        <div>Loading CSV...</div>
      ) : (
        <CSVLink
          data={csvData}
          headers={
            showMetrobi
              ? [
                  ...headers,
                  { label: 'metrobi_eligible', key: 'metrobiEligible' },
                ]
              : headers
          }
          filename={`locations_${nightEnd.viewing}_${selectedWarehouse?.name}.csv`}
          style={{
            color: 'black',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            textDecoration: 'none',
          }}
        >
          Download CSV
        </CSVLink>
      )}
    </Button>
  );
};

export default DownloadLocationsCsv;
